import { v4 as uuidv4 } from 'uuid';
import clientInfo from 'common/lib/clientInfo';
import { trackProductAnalytics } from 'common/services/product_analytics';
import { ProductEvents, TinymceInstance } from 'common/components/CopyMagic/CopyMagic.types';
import type { Editor as TipTapEditor } from '@tiptap/core';

const PUNCTUATION = /[!?.,"';:-<>@#$%^&*_~{}()[\]]/g;
const ONE_SPACE = ' ';

export const trackAnalytics =
  (productEvents: ProductEvents | undefined) =>
  (eventType: string, eventProperties = {}) => {
    const suffix = productEvents?.suffix ? ` - ${productEvents.suffix}` : '';
    const prefix = productEvents?.prefix ? `${productEvents.prefix} ` : '';
    const defaultEventPayload = productEvents?.payload ?? {};

    trackProductAnalytics(`${prefix}${eventType}${suffix}`, {
      'Site ID': clientInfo.get('site_id'),
      ...defaultEventPayload,
      ...eventProperties,
    });
  };

const handleObjectConversion = (
  rawContent: { content?: any; headlineContent?: any },
  trim: boolean,
) => {
  if (rawContent.content) {
    if (typeof rawContent.content !== 'string') return 0;

    return trim ? rawContent.content.trim() : rawContent.content;
  }

  if (typeof rawContent.headlineContent !== 'object') return 0;

  return trim
    ? Object.values(rawContent.headlineContent).join(' ').trim()
    : Object.values(rawContent.headlineContent).join(' ');
};

export const getDistinctWordsCount = (rawContent: any) => {
  if (!rawContent) return 0;

  const content = handleObjectConversion(rawContent, true);

  if (!content) return 0;

  const words = content.replace(/\s+/g, ONE_SPACE).replace(PUNCTUATION, '').split(ONE_SPACE);
  const distinctWords = new Set(words);

  return distinctWords.size;
};

export const getTextLength = (rawContent: any) => {
  if (!rawContent) return 0;

  const content = handleObjectConversion(rawContent, false);

  if (!content) return 0;

  return content.length;
};

export const getTokenCount = (content: string) =>
  // approximately 1 token per 4 characters
  Math.ceil(content.length / 4);
export const sumTokenCounts = (contents: Array<string>) => {
  let sum = 0;
  contents.forEach((content) => {
    sum += getTokenCount(content);
  });

  return sum;
};

// Two consecutive newlines are considered a paragraph and wrapped in <p> tags.
// One newline is considered a line break and a <br /> tag is appended.
const simpleTextFormat = (content: string) => {
  const paragraphSeparator = '\n\n';
  const newLineSeparator = '\n';

  return content
    .replace(/\r\n?/g, newLineSeparator)
    .split(paragraphSeparator)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, `${newLineSeparator}<br>`)}</p>`)
    .join(paragraphSeparator);
};

export const appendContentToEditor = (editor: TinymceInstance, content?: string) => {
  if (content) {
    // append generated content
    const currentContent = editor.getContent();

    editor.setContent(`${currentContent}${simpleTextFormat(content)}`);
    if (editor?.triggerSave) {
      editor.triggerSave();
    }
  }
};

export const appendContentToNewEditor = (editor: TipTapEditor, content?: string) => {
  if (content) {
    // append generated content
    const currentContent = editor.getHTML();
    editor.commands.setContent(`${currentContent}${simpleTextFormat(content)}`);
  }
};

export const getPagePathSpec = (url: string, sameOrigin: boolean = true) => {
  let pageUrl: URL;
  try {
    pageUrl = new URL(url);
  } catch (e) {
    return '';
  }
  if (sameOrigin && window.origin !== pageUrl.origin) return '';
  return pageUrl.pathname.replace(/\/\d+/g, '/:id');
};

export const generateSessionId = () => {
  const currentTimestampInSeconds = Math.floor(Date.now() / 1000);
  return `${currentTimestampInSeconds}-${uuidv4()}`;
};
